import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";

// Layout Components
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import FloatingCTA from "@/components/FloatingCTA";
import SEOHead from "@/components/SEOHead";
import Breadcrumbs from "@/components/Breadcrumbs";

// Pages
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/About";
import MaritimePage from "@/pages/MaritimePage";
import CarCrashesPage from "@/pages/CarCrashesPage";
import FirstResponderPage from "@/pages/FirstResponderPage";
import MotorcyclePage from "@/pages/MotorcyclePage";
import SlipFallPage from "@/pages/SlipFallPage";
import MedicalMalpracticePage from "@/pages/MedicalMalpracticePage";
import CaseResultRetainedSurgicalToolPage from "@/pages/CaseResultRetainedSurgicalToolPage";
import CaseResultRestaurantSlipFallPage from "@/pages/CaseResultRestaurantSlipFallPage";
import CaseResultsHubPage from "@/pages/CaseResultsHubPage";
import AreasPage from "@/pages/AreasPage";
import FAQPage from "@/pages/FAQPage";
import ContactPage from "@/pages/ContactPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import PricingPage from "@/pages/PricingPage";
import CompensationEstimatorPage from "@/pages/CompensationEstimatorPage";
import WrongfulDeathPage from "@/pages/WrongfulDeathPage";
import IsAmyRightForYouPage from "@/pages/IsAmyRightForYouPage";
import EverettSlipFallPage from "@/pages/EverettSlipFallPage";
import FemaleAttorneyPage from "@/pages/FemaleAttorneyPage";
import WomanPersonalInjuryLawyerPage from "@/pages/WomanPersonalInjuryLawyerPage";
import SoloPersonalInjuryLawyerPage from "@/pages/SoloPersonalInjuryLawyerPage";
import HowInsuranceCompaniesHandleClaimsPage from "@/pages/HowInsuranceCompaniesHandleClaimsPage";
import TacomaSlipFallPage from "@/pages/TacomaSlipFallPage";
import BellinghamSlipFallPage from "@/pages/BellinghamSlipFallPage";
import NotFoundPage from "@/pages/NotFoundPage";
import CityLandingPage from "@/pages/CityLandingPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import LegalGlossaryPage from "@/pages/LegalGlossaryPage";
import CredentialsPage from "@/pages/CredentialsPage";
import SitemapPage from "@/pages/SitemapPage";
import CityPracticeAreaPage from "@/pages/CityPracticeAreaPage";
import BackToTop from "@/components/BackToTop";

// Car Accident Landing Pages
import EverettCarAccidentPage from "@/pages/EverettCarAccidentPage";
import SnohomishCountyCarAccidentPage from "@/pages/SnohomishCountyCarAccidentPage";
import RearEndAccidentPage from "@/pages/RearEndAccidentPage";
import DrunkDriverAccidentPage from "@/pages/DrunkDriverAccidentPage";

// Car Accident Educational Pages
import WhatToDoAfterAccidentPage from "@/pages/WhatToDoAfterAccidentPage";
import ShouldITalkToInsurancePage from "@/pages/ShouldITalkToInsurancePage";
import ShouldIAcceptSettlementPage from "@/pages/ShouldIAcceptSettlementPage";
import HowMuchIsCaseWorthPage from "@/pages/HowMuchIsCaseWorthPage";
import HowLongDoesSettlementTakePage from "@/pages/HowLongDoesSettlementTakePage";
import WhenToHireLawyerPage from "@/pages/WhenToHireLawyerPage";

// Umbrella Personal Injury Pages
import EverettPersonalInjuryLawyerPage from "@/pages/EverettPersonalInjuryLawyerPage";

// Admin Pages
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminBlogPage from "@/pages/AdminBlogPage";
import AdminBlogEditorPage from "@/pages/AdminBlogEditorPage";

// Scroll to top on route change
import { useEffect } from "react";

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Handle old WordPress/Divi junk query parameters
const QueryParamRedirect = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Redirect any URL with et_core_page_resource to homepage
    if (location.search.includes('et_core_page_resource')) {
      window.location.replace('/');
    }
  }, [location]);

  return null;
};

// Layout wrapper that conditionally shows navbar/footer
const Layout = ({ children }) => {
  const { pathname } = useLocation();
  const isAdminRoute = pathname.startsWith('/admin');

  if (isAdminRoute) {
    return <>{children}</>;
  }

  // Show breadcrumbs on inner pages (not homepage)
  const showBreadcrumbs = pathname !== '/';

  return (
    <>
      <Navbar />
      {showBreadcrumbs && <Breadcrumbs />}
      <main id="main-content" tabIndex="-1">{children}</main>
      <Footer />
      <FloatingCTA />
      <BackToTop />
    </>
  );
};

function App() {
  return (
    <div className="App font-body">
      <BrowserRouter>
        <SEOHead />
        <ScrollToTop />
        <QueryParamRedirect />
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about-amy" element={<AboutPage />} />
            <Route path="/about" element={<Navigate to="/about-amy" replace />} />
            <Route path="/maritime-injuries" element={<MaritimePage />} />
            <Route path="/car-crashes" element={<CarCrashesPage />} />
            <Route path="/first-responder-crashes" element={<FirstResponderPage />} />
            <Route path="/motorcycle-crashes" element={<MotorcyclePage />} />
            <Route path="/slip-and-falls" element={<SlipFallPage />} />
            <Route path="/med-malpractice" element={<MedicalMalpracticePage />} />
            <Route path="/case-results" element={<CaseResultsHubPage />} />
            <Route path="/case-results/retained-surgical-tool-settlement" element={<CaseResultRetainedSurgicalToolPage />} />
            <Route path="/case-results/restaurant-slip-fall-wrist-fracture" element={<CaseResultRestaurantSlipFallPage />} />
            <Route path="/wrongful-death" element={<WrongfulDeathPage />} />
            <Route path="/is-amy-right-for-you" element={<IsAmyRightForYouPage />} />
            <Route path="/female-personal-injury-attorney" element={<Navigate to="/woman-personal-injury-lawyer" replace />} />
            <Route path="/woman-personal-injury-lawyer" element={<WomanPersonalInjuryLawyerPage />} />
            <Route path="/solo-personal-injury-lawyer" element={<SoloPersonalInjuryLawyerPage />} />
            <Route path="/how-insurance-companies-handle-claims" element={<HowInsuranceCompaniesHandleClaimsPage />} />
            <Route path="/everett-slip-and-fall-lawyer" element={<EverettSlipFallPage />} />
            <Route path="/tacoma-slip-and-fall-lawyer" element={<TacomaSlipFallPage />} />
            <Route path="/bellingham-slip-and-fall-lawyer" element={<BellinghamSlipFallPage />} />
            <Route path="/areas-we-serve" element={<AreasPage />} />
            <Route path="/faqs" element={<FAQPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/compensation-estimator" element={<CompensationEstimatorPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            
            {/* City Service Area Pages */}
            <Route path="/service-area/:slug" element={<CityLandingPage />} />
            
            {/* Legal Pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/legal-glossary" element={<LegalGlossaryPage />} />
            <Route path="/credentials" element={<CredentialsPage />} />
            <Route path="/about/credentials" element={<CredentialsPage />} />
            <Route path="/sitemap" element={<SitemapPage />} />
            <Route path="/lawyer/:slug" element={<CityPracticeAreaPage />} />
            
            {/* Car Accident Landing Pages */}
            <Route path="/everett-car-accident-lawyer" element={<EverettCarAccidentPage />} />
            <Route path="/snohomish-county-car-accident-lawyer" element={<SnohomishCountyCarAccidentPage />} />
            <Route path="/rear-end-accident-lawyer" element={<RearEndAccidentPage />} />
            <Route path="/drunk-driver-accident-lawyer" element={<DrunkDriverAccidentPage />} />
            
            {/* Car Accident Educational Pages */}
            <Route path="/what-to-do-after-car-accident" element={<WhatToDoAfterAccidentPage />} />
            <Route path="/should-i-talk-to-insurance" element={<ShouldITalkToInsurancePage />} />
            <Route path="/should-i-accept-settlement" element={<ShouldIAcceptSettlementPage />} />
            <Route path="/how-much-is-my-case-worth" element={<HowMuchIsCaseWorthPage />} />
            <Route path="/how-long-does-settlement-take" element={<HowLongDoesSettlementTakePage />} />
            <Route path="/when-to-hire-car-accident-lawyer" element={<WhenToHireLawyerPage />} />
            
            {/* Umbrella Personal Injury Pages */}
            <Route path="/everett-personal-injury-lawyer" element={<EverettPersonalInjuryLawyerPage />} />
            
            {/* ===========================================
               OLD WORDPRESS URL REDIRECTS (301s)
               These preserve SEO from the old site
            =========================================== */}
            
            {/* Old WordPress pages with number suffixes */}
            <Route path="/personal-injury-2" element={<Navigate to="/" replace />} />
            <Route path="/personal-injury-2/" element={<Navigate to="/" replace />} />
            
            {/* Old "Our Firm" and nested About pages */}
            <Route path="/our-firm/amy-c-brown-esq" element={<Navigate to="/about-amy" replace />} />
            <Route path="/our-firm/amy-c-brown-esq/" element={<Navigate to="/about-amy" replace />} />
            <Route path="/our-firm/*" element={<Navigate to="/about-amy" replace />} />
            <Route path="/about-amy-c-brown-esq" element={<Navigate to="/about-amy" replace />} />
            <Route path="/about-amy-c-brown-esq/" element={<Navigate to="/about-amy" replace />} />
            <Route path="/about-2" element={<Navigate to="/about-amy" replace />} />
            <Route path="/about-2/" element={<Navigate to="/about-amy" replace />} />
            <Route path="/contact-3" element={<Navigate to="/contact" replace />} />
            <Route path="/contact-3/" element={<Navigate to="/contact" replace />} />
            
            {/* Old blog and FAQ URLs */}
            <Route path="/blogs" element={<Navigate to="/blog" replace />} />
            <Route path="/blogs/" element={<Navigate to="/blog" replace />} />
            <Route path="/personal-injury-faq" element={<Navigate to="/faqs" replace />} />
            <Route path="/personal-injury-faq/" element={<Navigate to="/faqs" replace />} />
            
            {/* Old practice area URLs */}
            <Route path="/car-crash" element={<Navigate to="/car-crashes" replace />} />
            <Route path="/car-crash/" element={<Navigate to="/car-crashes" replace />} />
            <Route path="/cruise-ship-injuries" element={<Navigate to="/maritime-injuries" replace />} />
            <Route path="/cruise-ship-injuries/" element={<Navigate to="/maritime-injuries" replace />} />
            {/* Redirect from old/conflicting URL to new URL */}
            <Route path="/medical-malpractice" element={<Navigate to="/med-malpractice" replace />} />
            <Route path="/medical-malpractice/" element={<Navigate to="/med-malpractice" replace />} />
            
            {/* Old WordPress blog/content URLs - redirect to relevant pages */}
            <Route path="/how-can-a-lawyer-help-with-my-car-accident-case" element={<Navigate to="/car-crashes" replace />} />
            <Route path="/how-can-a-lawyer-help-with-my-car-accident-case/" element={<Navigate to="/car-crashes" replace />} />
            <Route path="/what-is-the-jones-act" element={<Navigate to="/maritime-injuries" replace />} />
            <Route path="/what-is-the-jones-act/" element={<Navigate to="/maritime-injuries" replace />} />
            <Route path="/construction-accidents" element={<Navigate to="/slip-and-falls" replace />} />
            <Route path="/construction-accidents/" element={<Navigate to="/slip-and-falls" replace />} />
            <Route path="/what-is-the-difference-between-a-minor-and-serious-car-accident-injury" element={<Navigate to="/car-crashes" replace />} />
            <Route path="/what-is-the-difference-between-a-minor-and-serious-car-accident-injury/" element={<Navigate to="/car-crashes" replace />} />
            <Route path="/what-is-wisha" element={<Navigate to="/first-responder-crashes" replace />} />
            <Route path="/what-is-wisha/" element={<Navigate to="/first-responder-crashes" replace />} />
            <Route path="/personal-injury-lawyer-marysville" element={<Navigate to="/areas-we-serve" replace />} />
            <Route path="/personal-injury-lawyer-marysville/" element={<Navigate to="/areas-we-serve" replace />} />
            <Route path="/how-common-are-ladder-injuries-on-commercial-vessels" element={<Navigate to="/maritime-injuries" replace />} />
            <Route path="/how-common-are-ladder-injuries-on-commercial-vessels/" element={<Navigate to="/maritime-injuries" replace />} />
            <Route path="/ferry-crew-injuries" element={<Navigate to="/maritime-injuries" replace />} />
            <Route path="/ferry-crew-injuries/" element={<Navigate to="/maritime-injuries" replace />} />
            <Route path="/personal-injury-lawyer-washington-debunking-claim-myths" element={<Navigate to="/blog" replace />} />
            <Route path="/personal-injury-lawyer-washington-debunking-claim-myths/" element={<Navigate to="/blog" replace />} />
            
            {/* More old WordPress URLs from GSC "Discovered" report */}
            <Route path="/about/" element={<Navigate to="/about-amy" replace />} />
            <Route path="/auto-accidents" element={<Navigate to="/car-crashes" replace />} />
            <Route path="/auto-accidents/" element={<Navigate to="/car-crashes" replace />} />
            <Route path="/bicycle-accidents" element={<Navigate to="/car-crashes" replace />} />
            <Route path="/bicycle-accidents/" element={<Navigate to="/car-crashes" replace />} />
            <Route path="/commercial-fishing-injuries" element={<Navigate to="/maritime-injuries" replace />} />
            <Route path="/commercial-fishing-injuries/" element={<Navigate to="/maritime-injuries" replace />} />
            <Route path="/dog-bites" element={<Navigate to="/" replace />} />
            <Route path="/dog-bites/" element={<Navigate to="/" replace />} />
            <Route path="/estate-planning" element={<Navigate to="/" replace />} />
            <Route path="/estate-planning/" element={<Navigate to="/" replace />} />
            <Route path="/factory-trawler-injuries" element={<Navigate to="/maritime-injuries" replace />} />
            <Route path="/factory-trawler-injuries/" element={<Navigate to="/maritime-injuries" replace />} />
            <Route path="/pedestrian-accidents" element={<Navigate to="/car-crashes" replace />} />
            <Route path="/pedestrian-accidents/" element={<Navigate to="/car-crashes" replace />} />
            <Route path="/personal-injury-lawyer-chelan-county" element={<Navigate to="/areas-we-serve" replace />} />
            <Route path="/personal-injury-lawyer-chelan-county/" element={<Navigate to="/areas-we-serve" replace />} />
            <Route path="/personal-injury-lawyer-skagit-county" element={<Navigate to="/areas-we-serve" replace />} />
            <Route path="/personal-injury-lawyer-skagit-county/" element={<Navigate to="/areas-we-serve" replace />} />
            <Route path="/personal-injury" element={<Navigate to="/" replace />} />
            <Route path="/personal-injury/" element={<Navigate to="/" replace />} />
            <Route path="/practice-areas" element={<Navigate to="/" replace />} />
            <Route path="/practice-areas/" element={<Navigate to="/" replace />} />
            <Route path="/truck-accidents" element={<Navigate to="/car-crashes" replace />} />
            <Route path="/truck-accidents/" element={<Navigate to="/car-crashes" replace />} />
            <Route path="/uncategorized" element={<Navigate to="/blog" replace />} />
            <Route path="/uncategorized/" element={<Navigate to="/blog" replace />} />
            <Route path="/winter-injuries" element={<Navigate to="/slip-and-falls" replace />} />
            <Route path="/winter-injuries/" element={<Navigate to="/slip-and-falls" replace />} />
            <Route path="/work-injuries" element={<Navigate to="/first-responder-crashes" replace />} />
            <Route path="/work-injuries/" element={<Navigate to="/first-responder-crashes" replace />} />
            
            {/* Female attorney search variations */}
            <Route path="/female-attorney" element={<Navigate to="/woman-personal-injury-lawyer" replace />} />
            <Route path="/female-attorney/" element={<Navigate to="/woman-personal-injury-lawyer" replace />} />
            <Route path="/woman-lawyer" element={<Navigate to="/woman-personal-injury-lawyer" replace />} />
            <Route path="/woman-lawyer/" element={<Navigate to="/woman-personal-injury-lawyer" replace />} />
            <Route path="/female-lawyer" element={<Navigate to="/woman-personal-injury-lawyer" replace />} />
            <Route path="/female-lawyer/" element={<Navigate to="/woman-personal-injury-lawyer" replace />} />
            
            {/* Common WordPress patterns that might exist */}
            <Route path="/reviews" element={<Navigate to="/" replace />} />
            <Route path="/reviews/" element={<Navigate to="/" replace />} />
            <Route path="/category/*" element={<Navigate to="/blog" replace />} />
            <Route path="/tag/*" element={<Navigate to="/blog" replace />} />
            <Route path="/author/*" element={<Navigate to="/about-amy" replace />} />
            <Route path="/page/*" element={<Navigate to="/" replace />} />
            
            {/* ===========================================
               OTHER COMMON URL VARIATIONS
            =========================================== */}
            <Route path="/maritime" element={<Navigate to="/maritime-injuries" replace />} />
            <Route path="/maritime-injury" element={<Navigate to="/maritime-injuries" replace />} />
            <Route path="/faq" element={<Navigate to="/faqs" replace />} />
            <Route path="/areas" element={<Navigate to="/areas-we-serve" replace />} />
            <Route path="/car-accidents" element={<Navigate to="/car-crashes" replace />} />
            <Route path="/car-accident" element={<Navigate to="/car-crashes" replace />} />
            <Route path="/car-accident-lawyer" element={<Navigate to="/car-crashes" replace />} />
            <Route path="/auto-accident" element={<Navigate to="/car-crashes" replace />} />
            <Route path="/first-responders" element={<Navigate to="/first-responder-crashes" replace />} />
            <Route path="/first-responder" element={<Navigate to="/first-responder-crashes" replace />} />
            <Route path="/motorcycle" element={<Navigate to="/motorcycle-crashes" replace />} />
            <Route path="/motorcycle-accident" element={<Navigate to="/motorcycle-crashes" replace />} />
            <Route path="/slip-fall" element={<Navigate to="/slip-and-falls" replace />} />
            <Route path="/slip-and-fall" element={<Navigate to="/slip-and-falls" replace />} />
            <Route path="/calculator" element={<Navigate to="/compensation-estimator" replace />} />
            <Route path="/personal-injury" element={<Navigate to="/" replace />} />
            <Route path="/jones-act" element={<Navigate to="/maritime-injuries" replace />} />
            <Route path="/seaman-injuries" element={<Navigate to="/maritime-injuries" replace />} />
            
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminLoginPage />} />
            <Route path="/admin/blog" element={<AdminBlogPage />} />
            <Route path="/admin/blog/new" element={<AdminBlogEditorPage />} />
            <Route path="/admin/blog/edit/:postId" element={<AdminBlogEditorPage />} />
            
            {/* 404 Catch-all */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </div>
  );
}

export default App;
